import React, { useState } from 'react';
import _, { isEmpty, map } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import { Grid, Checkbox, FormControl, ListItemText, MenuItem, Select } from '@mui/material';
import Button from '../../../components/Button';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import 'react-phone-number-input/style.css';
import styles from '../../../EditDevice/editDevice.styles.js';
import useStore from '../../../Store/Store.js';
import { createConversation } from '../../../utils/firestoreMessageConfigs.js';
import TextInput from '../../../components/TextInput.jsx';

const useStyles = makeStyles()(styles);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function AddConversations({ user, closeModal, caregivers }) {
  const { classes } = useStyles();
  const selectedRecipient = useStore((state) => state.selectedRecipient);
  const careRecipients = useStore((state) => state.careRecipients);
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const [chatUsers, setChatUsers] = React.useState([]);
  const [careCircle, setCareCircle] = React.useState([]);
  const [state, setState] = useState({
    message: '',
    errorMessage: '',
  });

  React.useEffect(() => {
    let caregiversData = [];
    if (!isEmpty(caregivers?.data)) {
      caregiversData = map(caregivers?.data, (item) => ({
        userId: item?.caregiverId?._id,
        name: item?.caregiverId?.userInfo?.firstName,
      }));
    }

    if (!isEmpty(careRecipients[selectedRecipient])) {
      caregiversData.push({
        userId: careRecipients[selectedRecipient]?.careRecipientId?._id,
        name: `${careRecipients[selectedRecipient]?.careRecipientId?.userInfo?.firstName} (${t('recipient')})`,
      });
    }

    setCareCircle(caregiversData);
  }, [caregivers, careRecipients, selectedRecipient, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { message } = state;
    if (isEmpty(message) && chatUsers.length > 1) {
      setState((prevState) => {
        return { ...prevState, errorMessage: t('chatNameRequired') };
      });
      return;
    }

    if (isEmpty(chatUsers)) {
      setState((prevState) => {
        return { ...prevState, errorMessage: t('selectAtLeastOneUser') };
      });
      return;
    }

    setState((prevState) => {
      return { ...prevState, errorMessage: '' };
    });

    const userIds = map(chatUsers, ({ userId }) => userId);

    userIds.push(user?._id);

    setLoader(true);
    try {
      const careCircleCRId = !isEmpty(selectedRecipient) ? selectedRecipient : user?._id;
      const data = await createConversation(userIds, careCircleCRId, message);

      if (!data?.success) {
        setLoader(false);
        setState((prevState) => {
          return {
            ...prevState,
            errorMessage: data?.message || t('somethingWentWrong'),
          };
        });
        return;
      }

      closeModal();
      setLoader(false);
    } catch (error) {
      console.log('error =====> ', error);
      setState((prevState) => {
        return {
          ...prevState,
          errorMessage: t('somethingWentWrong'),
        };
      });
      setLoader(false);
    }
  };

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    const counts = value.reduce(
      (acc, curr) => {
        acc.userId[curr.userId] = (acc.userId[curr.userId] || 0) + 1;
        acc.name[curr.name] = (acc.name[curr.name] || 0) + 1;
        return acc;
      },
      { userId: {}, name: {} },
    );

    const uniqueData = value.filter((item) => counts.userId[item.userId] === 1 && counts.name[item.name] === 1);

    setChatUsers(uniqueData);
  };

  const handleRenderSelectValue = React.useCallback((selected) => {
    const names = map(selected, ({ name }) => name);
    return names.join(', ');
  }, []);

  return (
    <div className={classes.paper}>
      <Grid container item direction="row" justify="space-between" alignItems="center" className={classes.header}>
        <Grid item className={classes.title}>
          {t('addConversation')}
        </Grid>
        <Grid item className={classes.closeIcon} onClick={closeModal}>
          <CloseIcon />
        </Grid>
      </Grid>

      <Grid item className={classes.userInfoPanel} style={{ marginTop: '20px' }}>
        <div className={classes.newCaregiver}>
          <Grid item className={classes.deviceInfoPart}>
            <div className={classes.deviceInfoPartTitle}>{t('user')}</div>
            <FormControl variant="outlined" style={{ width: '396px', marginTop: '10px' }}>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={chatUsers}
                onChange={handleChange}
                renderValue={handleRenderSelectValue}
                MenuProps={MenuProps}
                style={{ borderRadius: 100 }}
              >
                {isEmpty(careCircle)
                  ? map(
                      [
                        {
                          userId: '',
                          name: t('noUserFound'),
                        },
                      ],
                      ({ name }, idx) => (
                        <option key={idx} value="" style={{ marginLeft: '10px' }}>
                          {name}
                        </option>
                      ),
                    )
                  : map(careCircle, ({ userId, name }, idx) => (
                      <MenuItem key={idx} value={{ userId, name }}>
                        <Checkbox checked={chatUsers.some((person) => person.userId === userId)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </Grid>
        </div>
      </Grid>

      {chatUsers.length > 1 && (
        <Grid item className={classes.userInfoPanel} style={{ marginTop: '20px' }}>
          <div className={classes.newCaregiver}>
            <Grid item className={classes.deviceInfoPart}>
              <TextInput
                label={t('chatName')}
                fullWidth
                value={state.message}
                onChange={(e) =>
                  setState((prevState) => {
                    return { ...prevState, message: e.target.value };
                  })
                }
              />
            </Grid>
          </div>
        </Grid>
      )}

      <Grid
        item
        direction="column"
        justify="flex-start"
        alignItems="center"
        className={classes.errorMessage}
        style={{ color: 'red', marginTop: '10px' }}
      >
        {state.errorMessage}
      </Grid>
      <Grid container item direction="row" justify="flex-end" alignItems="center" className={classes.footer}>
        <Grid item style={{ marginRight: '10px' }}>
          <Button fullWidth text={t('cancel')} onClick={closeModal} bg="light" />
        </Grid>
        <Grid item>
          <Button loading={loader} fullWidth text={t('create')} onClick={handleSubmit} />
        </Grid>
      </Grid>
    </div>
  );
}

export default AddConversations;
